<script setup lang="ts">
import type { RouteLocationRaw } from '#vue-router'
import type { LiteratureDto } from '~/utils/solr/literature'
import { faLockKeyhole } from '@fortawesome/pro-light-svg-icons'

defineProps<{
  literature: LiteratureDto[]
  isFavorite: boolean
  isLoading?: boolean
  toggleFavorite: (category: 'law' | 'literature', id: string) => void
}>()

const hasAccess = useHasContentLicense()
const { coverFallbackImage, shouldRenderCoverImage, handleCoverImageError } = useCoverImageFallbackMap()

function getItemUrl(item: LiteratureDto) {
  if (item.type === 'Fagbok') {
    return hasAccess.value ? { name: 'book-by-id', params: { bokId: [item.id] } } satisfies RouteLocationRaw : { name: 'book-about-by-id', params: { bokId: item.id } } satisfies RouteLocationRaw
  }

  return item.remoteId ? { name: 'academic-article-by-id', params: { artikkelId: [item.remoteId] } } satisfies RouteLocationRaw : undefined
}

function getItemDate(item: LiteratureDto) {
  if (item.type === 'Fagbok') {
    if (item.edition) {
      return `${item.edition}. utgave ${item.lastModified}`
    }
    return `Utgave: ${item.lastModified}`
  }

  return `Sist endret: ${item.lastModified}`
}
</script>

<template>
  <Table :aria-label="`Faglitteratur ${isLoading ? '- Laster' : ''}`" class="flex flex-col w-full">
    <template #rows>
      <TableBody class="space-y-2xs">
        <template v-if="isLoading">
          <TableRow v-for="n in 10" :key="`skeleton-${n}`" is-loading class="p-s">
            <LiteratureListSkeleton />
          </TableRow>
        </template>

        <template v-else>
          <TableRow v-for="item in literature" :key="item.id">
            <NuxtLink
              :to="getItemUrl(item)"
              :aria-label="item.title"
              class="group w-full flex no-underline px-m py-s gap-m min-h-32 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blurple-300"
            >
              <div class="flex w-full flex-col md:flex-row gap-sm md:gap-l 2xl:gap-xl">
                <div class="flex w-full flex-col gap-1 md:max-w-[226px]">
                  <h3 class="font-semibold title-s text-primary">{{ item.title }}</h3>
                  <span v-if="item.subtitle" class="text-base text-primary">
                    {{ item.subtitle }}
                  </span>
                </div>

                <div class="flex w-full flex-col text-xsmall gap-3xs md:max-w-[226px]">
                  <span v-for="author in item.authors" :key="author">{{ author }}</span>
                </div>
                <div class="flex flex-col justify-start gap-3xs md:max-w-[226px]">
                  <span v-if="item.legalAreas && item.legalAreas.length" class="text-caption text-secondary">
                    Rettsområde: {{ item.legalAreas.join(', ') }}
                  </span>
                  <span v-if="item.lastModified" class="text-caption text-secondary">
                    {{ getItemDate(item) }}
                  </span>
                  <Chip size="small" variant="filled" :aria-label="item.type" color="carbon" class="mt-1 group-hover:bg-blurple-100 group-active:bg-blurple-200">
                    {{ item.type }}
                  </Chip>
                </div>
              </div>
              <div class="flex justify-end gap-l items-start md:items-center">
                <NuxtImg
                  v-if="item.type === 'Fagbok' && shouldRenderCoverImage(item.id, item.coverImage)"
                  :src="item.coverImage"
                  :alt="`Bokomslag: ${item.title}`"
                  height="96"
                  width="68"
                  quality="20"
                  loading="lazy"
                  class="h-24 w-auto object-cover hidden rounded-lg md:flex"
                  @error="handleCoverImageError(item.id)"
                />
                <img
                  v-else-if="item.type === 'Fagbok'"
                  :src="coverFallbackImage"
                  :alt="`Bokomslag: ${item.title}`"
                  class="h-24 w-auto object-cover hidden rounded-lg md:flex"
                >
                <FavoriteButton v-if="hasAccess" intent="outlined" :selected="item.isFavorite" :aria-label="item.isFavorite ? `Fjern ${item.title} fra favoritter` : `Legg til ${item.title} i favoritter`" @click.prevent="toggleFavorite('literature', item.id)" />
                <Icon v-else :icon="faLockKeyhole" />
              </div>
            </NuxtLink>
          </TableRow>
        </template>
      </TableBody>
    </template>
  </Table>
</template>
